import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, Button, Box, Avatar, CircularProgress, List, Chip, TextField } from '@mui/material';
import { CheckCircle, CloudUpload, FolderSpecial, Security } from '@mui/icons-material';

interface ParsedFile { name: string; type: string; base64: string; }

const DocumentVault: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const queryLeadId = searchParams.get('id') || searchParams.get('leadId') || '';
  const queryName = searchParams.get('name') || '';
  const queryCat = searchParams.get('cat') || '';

  const [activeLeadId, setActiveLeadId] = useState<string>(
    queryLeadId || localStorage.getItem("fingard_lead_id") || ""
  );
  const [manualInputId, setManualInputId] = useState<string>('');
  const storedName = queryName || localStorage.getItem("fingard_client_name") || "";
  
  const { 
    serviceCategory = queryCat || 'general', 
    serviceTitle = 'Service', 
    checklist = [
      "Identity Proof (PAN / Aadhaar)",
      "Bank Statement (Last 6 Months) / Cancelled Cheque",
      "Form 16 / Salary Slips / Invoices",
      "Business / Other Supporting Documents"
    ] 
  } = location.state || {};

  const [selectedFiles, setSelectedFiles] = useState<Record<string, File>>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadComplete, setUploadComplete] = useState<boolean>(false);

  const handleDocumentUpload = async () => {
    if (Object.keys(selectedFiles).length === 0) return;
    setIsUploading(true);

    try {
      const uploadedFilesPromises = Object.entries(selectedFiles).map(([docName, file]) => {
        return new Promise<ParsedFile>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = (reader.result as string).split(',')[1];
            resolve({ name: `${docName}_${file.name}`, type: file.type, base64: base64String });
          };
          reader.readAsDataURL(file);
        });
      });

      const parsedFiles = await Promise.all(uploadedFilesPromises);

      const apiBase = process.env.REACT_APP_API_URL || 
        (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          ? 'http://localhost:5001'
          : 'https://fingard-backend.onrender.com');

      const resp = await fetch(`${apiBase}/api/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          leadId: activeLeadId, 
          name: storedName || 'Client', 
          serviceCategory: serviceCategory,
          files: parsedFiles 
        })
      });

      const resultData = await resp.json();
      if (!resp.ok || !resultData.success) {
        throw new Error(resultData.error || 'Server rejected file upload batch.');
      }
      
      setUploadComplete(true);
    } catch (err: any) {
      alert(`Upload system mismatch: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh', py: 8, display: 'flex', alignItems: 'center', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'2\' cy=\'2\' r=\'1.5\' fill=\'%230b2847\' fill-opacity=\'0.03\'/%3E%3C/svg%3E")' }}>
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: { xs: 4, md: 5 }, borderRadius: '24px', border: '1px solid #E2E8F0', bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)', textAlign: 'center', boxShadow: '0px 20px 40px rgba(15, 23, 42, 0.05)' }}>
          <Avatar sx={{ bgcolor: 'rgba(11, 40, 71, 0.05)', color: '#123D6B', mx: 'auto', width: 64, height: 64, mb: 3 }}><FolderSpecial sx={{ fontSize: 32 }} /></Avatar>
          
          {!activeLeadId ? (
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#123D6B', letterSpacing: '-0.02em' }}>
                Client Document Vault
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B', mb: 3.5, lineHeight: 1.6 }}>
                Please enter your <strong>Lead Reference ID</strong> (e.g. <code>FG-2026...</code>) received on WhatsApp or from your CA to upload your documents.
              </Typography>
              <TextField
                fullWidth
                size="medium"
                label="Lead Reference ID"
                placeholder="e.g. FG-20260916-ABCD"
                value={manualInputId}
                onChange={(e) => setManualInputId(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={!manualInputId.trim()}
                onClick={() => setActiveLeadId(manualInputId.trim().toUpperCase())}
                sx={{ bgcolor: '#123D6B', color: '#FFF', py: 1.5, fontWeight: 700, borderRadius: '12px', textTransform: 'none', '&:hover': { bgcolor: '#C59B27' } }}
              >
                Access My Vault
              </Button>
            </Box>
          ) : !uploadComplete ? (
            <Box>
              <Chip label={serviceTitle} size="small" sx={{ mb: 2, bgcolor: '#F1F5F9', color: '#475569', fontWeight: 600 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#123D6B', letterSpacing: '-0.02em' }}>Secure Document Vault</Typography>
              <Typography variant="body2" sx={{ color: '#64748B', mb: 4 }}>
                Reference tracking assigned: <strong style={{ color: '#C59B27' }}>{activeLeadId}</strong>
              </Typography>

              <List disablePadding sx={{ mb: 4, textAlign: 'left' }}>
                {checklist.map((docName: string, idx: number) => {
                  const isSelected = !!selectedFiles[docName];
                  return (
                    <Paper key={idx} variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: '14px', borderColor: isSelected ? '#1E5631' : '#E2E8F0', bgcolor: isSelected ? 'rgba(30, 86, 49, 0.01)' : '#FFFFFF', transition: 'all 0.2s', '&:hover': { borderColor: isSelected ? '#1E5631' : '#C59B27' } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CheckCircle sx={{ color: isSelected ? '#1E5631' : '#CBD5E1', fontSize: 24 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#123D6B' }}>{docName}</Typography>
                            {isSelected && (
                              <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.5 }}>
                                {selectedFiles[docName].name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Button variant="contained" component="label" disableElevation size="small" sx={{ minWidth: 110, bgcolor: isSelected ? '#1E5631' : '#123D6B', color: '#FFFFFF', px: 2, py: 0.8, borderRadius: '8px', fontWeight: 600, fontSize: '13px', textTransform: 'none', '&:hover': { bgcolor: isSelected ? '#153E22' : '#C59B27' } }}>
                          {isSelected ? "Change File" : "Choose File"}
                          <input type="file" hidden onChange={(e) => {
                            if (e.target.files?.[0]) {
                              setSelectedFiles(prev => ({ ...prev, [docName]: e.target.files![0] }));
                            }
                          }} />
                        </Button>
                      </Box>
                    </Paper>
                  );
                })}
              </List>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3, color: '#64748B' }}>
                <Security sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>Files are encrypted and routed directly to secure cloud storage.</Typography>
              </Box>

              <Button fullWidth variant="contained" size="large" disableElevation onClick={handleDocumentUpload} disabled={Object.keys(selectedFiles).length === 0 || isUploading} sx={{ bgcolor: '#123D6B', color: '#FFFFFF', py: 1.8, borderRadius: '12px', fontWeight: 700, textTransform: 'none', fontSize: '15px', '&:hover': { bgcolor: '#C59B27' }, '&:disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' } }}>
                {isUploading ? <CircularProgress size={24} color="inherit" /> : <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CloudUpload /> Stream Documents to Vault</Box>}
              </Button>
            </Box>
          ) : (
            <Box sx={{ py: 3 }}>
              <Avatar sx={{ bgcolor: 'rgba(30, 86, 49, 0.1)', color: '#1E5631', mx: 'auto', width: 68, height: 68, mb: 2 }}><CheckCircle sx={{ fontSize: 36 }} /></Avatar>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#123D6B', mb: 1.5 }}>Documents Uploaded!</Typography>
              <Typography variant="body2" sx={{ color: '#64748B', mb: 4, maxWidth: '400px', mx: 'auto', lineHeight: 1.6 }}>
                Your documents have been securely saved to Fingard Vault for Lead Reference: <br/><strong style={{ color: '#C59B27' }}>{activeLeadId}</strong>.<br/>
                Our CA team will review and verify them shortly.
              </Typography>
              <Button variant="contained" disableElevation onClick={() => { localStorage.clear(); navigate('/'); }} sx={{ bgcolor: '#123D6B', color: '#FFFFFF', px: 5, py: 1.5, borderRadius: '10px', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#C59B27' } }}>Return Home</Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default DocumentVault;
